/**
 * Distillery API 타입 정의
 * 어드민 증류소 관련 API 스펙
 */

// ============================================
// API 엔드포인트 정의
// ============================================

export const DistilleryApi = {
  /** 증류소 목록 조회 */
  list: {
    endpoint: '/admin/api/v1/distilleries',
    method: 'GET',
  },
  /** 증류소 상세 조회 */
  detail: {
    endpoint: '/admin/api/v1/distilleries/:id',
    method: 'GET',
  },
  /** 증류소 생성 */
  create: {
    endpoint: '/admin/api/v1/distilleries',
    method: 'POST',
  },
  /** 증류소 수정 */
  update: {
    endpoint: '/admin/api/v1/distilleries/:id',
    method: 'PUT',
  },
  /** 증류소 삭제 */
  delete: {
    endpoint: '/admin/api/v1/distilleries/:id',
    method: 'DELETE',
  },
  /** 증류소 정렬 순서 변경 */
  updateSortOrder: {
    endpoint: '/admin/api/v1/distilleries/:id/sort-order',
    method: 'PATCH',
  },
} as const;

// ============================================
// API 타입 정의
// ============================================

export interface DistilleryApiTypes {
  /** 증류소 목록 조회 */
  list: {
    /** 요청 파라미터 */
    params: {
      /** 검색어 */
      keyword?: string;
      /** 페이지 번호 (기본값: 0) */
      page?: number;
      /** 페이지 크기 (기본값: 20) */
      size?: number;
      /** 정렬 방향 (기본값: ASC) */
      sortOrder?: 'ASC' | 'DESC';
    };
    /** 응답 아이템 */
    response: {
      /** 증류소 ID */
      id: number;
      /** 증류소 한글명 */
      korName: string;
      /** 증류소 영문명 */
      engName: string;
      /** 이미지 URL */
      imageUrl: string | null;
      /** 생성일시 */
      createdAt: string;
      /** 수정일시 */
      modifiedAt: string;
      /** 정렬 순서 (미설정: 9999) */
      sortOrder: number;
    };
    /** 페이지네이션 메타 정보 */
    meta: {
      /** 현재 페이지 */
      page: number;
      /** 페이지 크기 */
      size: number;
      /** 전체 요소 수 */
      totalElements: number;
      /** 전체 페이지 수 */
      totalPages: number;
      /** 다음 페이지 존재 여부 */
      hasNext: boolean;
    };
  };
  /** 증류소 상세 조회 */
  detail: {
    /** 응답 */
    response: {
      /** 증류소 ID */
      id: number;
      /** 증류소 한글명 */
      korName: string;
      /** 증류소 영문명 */
      engName: string;
      /** 이미지 URL */
      imageUrl: string | null;
      /** 생성일시 */
      createdAt: string;
      /** 수정일시 */
      modifiedAt: string;
      /** 정렬 순서 (미설정: 9999) */
      sortOrder: number;
    };
  };
  /** 증류소 생성/수정 요청 */
  form: {
    /** 요청 데이터 */
    request: {
      /** 증류소 한글명 */
      korName: string;
      /** 증류소 영문명 */
      engName: string;
      /** 이미지 URL */
      imageUrl?: string | null;
      /** 정렬 순서 (미지정 시 백엔드 기본값 9999) */
      sortOrder?: number | null;
    };
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 생성/수정된 증류소 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
  /** 증류소 삭제 */
  delete: {
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 삭제된 증류소 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
  /** 증류소 정렬 순서 변경 */
  updateSortOrder: {
    /** 요청 데이터 */
    request: {
      /** 정렬 순서 (0 이상) */
      sortOrder: number;
    };
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 대상 증류소 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
}

// ============================================
// 헬퍼 타입
// ============================================

/** 증류소 목록 조회 파라미터 */
export type DistillerySearchParams = DistilleryApiTypes['list']['params'];

/** 증류소 목록 아이템 */
export type DistilleryListItem = DistilleryApiTypes['list']['response'];

/** 증류소 목록 페이지네이션 메타 */
export type DistilleryPageMeta = DistilleryApiTypes['list']['meta'];

/** 증류소 상세 */
export type DistilleryDetail = DistilleryApiTypes['detail']['response'];

/** 증류소 폼 데이터 (생성/수정) */
export type DistilleryFormData = DistilleryApiTypes['form']['request'];

/** 증류소 생성/수정 응답 */
export type DistilleryFormResponse = DistilleryApiTypes['form']['response'];

/** 증류소 삭제 응답 */
export type DistilleryDeleteResponse = DistilleryApiTypes['delete']['response'];

/** 증류소 정렬 순서 변경 요청 */
export type DistillerySortOrderRequest = DistilleryApiTypes['updateSortOrder']['request'];

/** 증류소 정렬 순서 변경 응답 */
export type DistillerySortOrderResponse = DistilleryApiTypes['updateSortOrder']['response'];
